/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: SecuritySettingsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Security Settings module.
----------------------------------------------------------------*/

import React, { useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Save } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useContextStore } from '../../store/context';
import ModuleFormHeader from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Toggle } from '../../components/ui/Toggle';
import { SecretInput } from '../../components/ui/SecretField';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import * as api from './api';

export default function SecuritySettingsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();
    const { register, control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<api.SecuritySettings>({
        defaultValues: {
            enabled: false,
            username: '',
            password: '',
            domainId: 0
        }
    });



    const loadSettings = useCallback(async () => {
        try {
            const data = await api.getSecuritySettings();
            reset(data);
        } catch (error) {
            console.error('Failed to load security settings', error);
            showError(t('provisioningSecurity.loadError'));
        }
    }, [reset, showError, t]);

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (selectedDomain) {
            setValue('domainId', selectedDomain.id);
        }
    }, [selectedDomain, setValue]);

    const onSubmit = async (data: api.SecuritySettings) => {
        try {
            await api.updateSecuritySettings(data);
            showSuccess(t('provisioningSecurity.success'));
            loadSettings();
        } catch (error) {
            console.error('Failed to update security settings', error);
            showError(t('provisioningSecurity.error'));
        }
    };

    // ... inside render ...



    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<ShieldCheck className="h-5 w-5 text-primary" />}
                title={t('provisioningSecurity.title')}
                backButtonText={t('common.exit', 'Exit')}
                onBack={() => navigate(-1)}
            />

            <div className="mt-8">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <input type="hidden" {...register('domainId')} />
                    <FormBox title={t('provisioningSecurity.authSettings')} icon={<ShieldCheck className="h-4 w-4 text-primary" />}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">

                            {/* Row 1: Username and Password */}

                            {/* Username */}
                            <SimpleTooltip content={t('provisioningSecurity.usernameTooltip')}>
                                <Label required>{t('provisioningSecurity.username')}</Label>
                            </SimpleTooltip>
                            <Input
                                {...register('username', { required: t('common.required', 'Required') })}
                                placeholder="e.g. admin"
                                error={errors.username?.message}
                            />

                            {/* Password */}
                            <SimpleTooltip content={t('provisioningSecurity.passwordTooltip')}>
                                <Label htmlFor="password" required>{t('provisioningSecurity.password')}</Label>
                            </SimpleTooltip>
                            <Controller
                                name="password"
                                control={control}
                                rules={{ required: t('common.required', 'Required') }}
                                render={({ field: { onChange, value } }) => (
                                    <SecretInput
                                        value={value || ''}
                                        onChange={onChange}
                                        placeholder="��������"
                                        className={errors.password ? 'border-red-500' : ''}
                                    />
                                )}
                            />

                            {/* Row 2: Enable Toggle */}
                            <Label htmlFor="enabled" className="text-left">{t('provisioningSecurity.enable')}</Label>
                            <div className="flex items-center">
                                <Controller
                                    name="enabled"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Toggle
                                            id="enabled"
                                            value={!!value}
                                            onChange={(newValue) => onChange(newValue)}
                                        />
                                    )}
                                />
                            </div>

                            {/* Spacer columns if needed, or let the note take over in next block */}
                            <div className="col-span-2"></div>

                            {/* Note Section */}
                            <div className="col-span-4 mt-4 text-sm text-muted-foreground bg-muted/30 p-4 rounded-md">
                                <p><strong>{t('provisioningSecurity.note')}</strong> {t('provisioningSecurity.noteContent1')}</p>
                                <p className="mt-1">{t('provisioningSecurity.noteContent2')}</p>
                            </div>
                        </div>
                    </FormBox>
                </form>
            </div>

            <FixedActionBar
                onSave={handleSubmit(onSubmit)}
                isSaving={isSubmitting}
                saveText={t('common.save', 'Save')}
                onCancel={loadSettings}
            />
        </div>
    );
}
